import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { AppDispatch, RootState } from 'store';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { clearAddNewPanel } from 'store/slices/QueryParamsSlice';
import { addPanelLayout } from 'components/DisplayEnergy/PanelSelector/api';
import { errorResponseHandler } from 'store/api/AxiosError';
import { appToast } from 'components/Basic/CustomToast';
import useStartShadingRecalc from './useStartShadingRecalc';

/**
 * Brings the design onto the `panel-key` the parent asked for, when it loaded on a different one.
 *
 * Gated on `add-new-panel=true`: panel-key on its own has other consumers that must stay read-only.
 *
 * panel.key is the panel the *processed layout* is built from, not the one requested, so the
 * mismatch is both the trigger and the reload guard - it clears only once the layout really moved.
 *
 * Returns true while the swap is in flight: until changePanel lands the store still holds the old
 * design, so callers must show a loader rather than render it.
 */
export default function useAutoAddPanelOnLoad(): boolean {
	const dispatch = useDispatch<AppDispatch>();
	const { addNewPanel, panelKey } = useSelector((state: RootState) => state.QueryParams.data);
	const { loading, data: roofData } = useSelector((state: RootState) => state.roofData);
	const { uuid, designType, panel } = roofData;
	const startShadingRecalc = useStartShadingRecalc();
	const [searchParams, setSearchParams] = useSearchParams();

	const needsPanelApplied = Boolean(
		addNewPanel && panelKey && uuid && !loading && designType === 'SOLAR'
		&& panel?.key?.toUpperCase() !== panelKey.toUpperCase()
	);

	// Both calls sit in the query fn so either one failing lands on isError.
	const { isError: applyFailed } = useQuery(
		['auto-add-panel-layout', uuid, panelKey],
		async () => {
			const shadingStatus = await addPanelLayout({ uuid, panelKey: panelKey as string, resetPanelKey: false});
			const reread = dispatch(changePanel({ uuid, panelKey: panelKey as string, designType }));
			// before the await, like PanelSelector: sets isRecomputing so dsmShadingListener's
			// changePanel.fulfilled handler bails instead of enqueuing a second run
			startShadingRecalc(shadingStatus);
			await reread.unwrap();
			// after the re-read, so the loader stays up until the new layout is in the store. Drop it
			// from the URL too - Home re-reads that on any nav back and would flip the flag on again.
			dispatch(clearAddNewPanel());
			const nextParams = new URLSearchParams(searchParams);
			nextParams.delete('add-new-panel');
			setSearchParams(nextParams, { replace: true });
		},
		{
			enabled: needsPanelApplied,
			retry: false, // rewrites the layout - never replay it behind the user's back
			cacheTime: 0,
			refetchOnWindowFocus: false,
			onError: (err) => {
				const { message } = errorResponseHandler(err as AxiosError);
				appToast.error(message || 'Something went wrong while changing panel.', {
					toastId: 'auto-add-panel-error',
				});
			},
		}
	);

	return needsPanelApplied && !applyFailed;
}
